import { useState } from 'react';
import { useLang } from '@/context/LanguageContext';
import { Input } from '@/components/ui/input';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const { t } = useLang();
  const [email, setEmail] = useState('');
  const [nlStatus, setNlStatus] = useState(null);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/newsletter`, { email });
      setNlStatus('success');
      setEmail('');
    } catch {
      setNlStatus('error');
    }
  };

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer data-testid="footer-section" className="bg-zinc-950 border-t border-white/5">
      {/* Newsletter */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end">
          <div>
            <h3 className="text-3xl lg:text-4xl font-normal tracking-normal text-purple-400 mb-3" style={{ fontFamily: "'Great Vibes', cursive" }}>
              {t('newsletter.title')}
            </h3>
            <p className="text-base font-manrope text-zinc-400">
              {t('newsletter.desc')}
            </p>
          </div>
          <form onSubmit={handleNewsletter} data-testid="newsletter-form" className="flex gap-0">
            <Input
              data-testid="newsletter-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder={t('newsletter.placeholder')}
              className="bg-transparent border-white/20 text-zinc-50 placeholder:text-zinc-600 rounded-none h-12 flex-1 focus:border-white focus:ring-white"
            />
            <button
              data-testid="newsletter-submit"
              type="submit"
              className="bg-white text-black px-6 h-12 text-sm font-semibold uppercase tracking-wider hover:bg-zinc-200 transition-colors flex items-center gap-2 shrink-0"
            >
              {t('newsletter.cta')}
              <ArrowRight size={14} />
            </button>
          </form>
        </div>
        {nlStatus === 'success' && (
          <p data-testid="newsletter-success" className="text-emerald-400 text-sm mt-4">{t('newsletter.success')}</p>
        )}
        {nlStatus === 'error' && (
          <p data-testid="newsletter-error" className="text-red-400 text-sm mt-4">{t('newsletter.error')}</p>
        )}
      </div>

      {/* Big brand logo */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-12 flex justify-center">
        <img
          src="https://customer-assets.emergentagent.com/job_madison-fred-design/artifacts/zy0qofhd_Copilot_20260414_133202.png"
          alt="MadisonFred"
          className="w-[60vw] lg:w-[40vw] max-w-2xl h-auto opacity-80"
        />
      </div>

      {/* Contact info bar */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 border-t border-white/5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div data-testid="footer-address">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-600 mb-2">
              {t('contact.addressLabel')}
            </p>
            <p className="text-sm font-manrope text-zinc-400">
              42, avenue Montaigne<br />75008 Paris
            </p>
          </div>
          <div data-testid="footer-whatsapp">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-600 mb-2">WhatsApp</p>
            <a href="https://wa.me/33611563617" target="_blank" rel="noopener noreferrer" className="block text-sm font-manrope text-zinc-400 hover:text-zinc-50 transition-colors">
              +33 (0) 6 11 56 36 17
            </a>
            <a href="https://wa.me/33766560698" target="_blank" rel="noopener noreferrer" className="block text-sm font-manrope text-zinc-400 hover:text-zinc-50 transition-colors mt-1">
              +33 (0) 7 66 56 06 98
            </a>
          </div>
          <div data-testid="footer-email-info">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-600 mb-2">Email</p>
            <a href="mailto:contact@madisonfred.com" className="block text-sm font-manrope text-zinc-400 hover:text-zinc-50 transition-colors">
              contact@madisonfred.com
            </a>
          </div>
        </div>
        <div className="flex items-center gap-6 mt-6 flex-wrap">
            {['home', 'services', 'portfolio', 'about', 'contact'].map((item) => (
              <button
                key={item}
                data-testid={`footer-nav2-${item}`}
                onClick={() => scrollTo(item)}
                className="text-xs text-zinc-500 hover:text-zinc-50 transition-colors uppercase tracking-wider"
              >
                {t(`nav.${item}`)}
              </button>
            ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-sm text-zinc-500 font-manrope">
          {t('footer.tagline')}
        </p>
        <p className="text-sm text-zinc-600 font-manrope">
          &copy; {new Date().getFullYear()} MadisonFred. {t('footer.rights')}
        </p>
      </div>
    </footer>
  );
}
